import { Link, router } from "@inertiajs/react";
import { ArrowLeft, CheckCircle, Play, Zap } from "lucide-react";
import { useEffect } from "react";
import AppLayout from "../../layouts/AppLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Candidate, ExcludedFirm, PackageData, SkmBlast, SubmissionRow } from "./_types";
import ResumeSection from "./ResumeSection";
import SubmissionsSection from "./SubmissionsSection";
import SkmSection from "./SkmSection";

interface Props {
  package: PackageData;
  candidate: Candidate;
  submissions: SubmissionRow[];
  skm_blast: SkmBlast | null;
  excluded_firms: ExcludedFirm[];
  ai_assist: boolean;
}

const STATUS_BADGE: Record<string, string> = {
  not_started:      "bg-gray-100 text-gray-500",
  generating:       "bg-blue-100 text-blue-600",
  ready_for_review: "bg-yellow-100 text-yellow-700",
  approved:         "bg-green-100 text-green-700",
  executing:        "bg-purple-100 text-purple-700",
  completed:        "bg-green-200 text-green-800",
  failed:           "bg-red-100 text-red-600",
};

const STATUS_LABELS: Record<string, string> = {
  not_started:      "Not Started",
  generating:       "Generating...",
  ready_for_review: "Ready for Review",
  approved:         "Approved",
  executing:        "Executing...",
  completed:        "Completed",
  failed:           "Failed",
};

export default function PackageShow({ package: pkg, candidate, submissions, skm_blast, excluded_firms, ai_assist }: Props) {
  // Poll while job is running so the page updates automatically
  const isRunning = pkg.status === "generating" || pkg.status === "executing";
  useEffect(() => {
    if (!isRunning) return;
    const timer = setTimeout(() => router.reload(), 3000);
    return () => clearTimeout(timer);
  }, [isRunning, pkg.status]);

  function generatePackage() {
    router.post(`/candidates/${candidate.id}/package/generate`);
  }

  function approvePackage() {
    router.post(`/candidates/${candidate.id}/package/approve`);
  }

  const canGenerate = pkg.status === "not_started" || pkg.status === "failed";
  const canApprove  = pkg.status === "ready_for_review" && pkg.all_sections_complete;

  const checklist = [
    { label: "Resume reviewed",      done: pkg.resume_status === "approved" || pkg.resume_status === "skipped" },
    { label: "SKM blast reviewed",   done: pkg.skm_status === "approved" || pkg.skm_status === "skipped" },
    { label: "Submissions reviewed", done: pkg.submissions_status === "approved" || pkg.submissions_status === "skipped" },
  ];

  return (
    <AppLayout title={`Package — ${candidate.full_name}`}>
      <Link href={`/candidates/${candidate.id}`} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> {candidate.full_name}
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Submission Package</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {[candidate.candidate_type, candidate.firm_name, candidate.location].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_BADGE[pkg.status] ?? "bg-gray-100 text-gray-500"}`}>
            {STATUS_LABELS[pkg.status] ?? pkg.status}
          </span>
          {canGenerate && (
            <Button size="sm" onClick={generatePackage} variant="outline">
              <Play className="w-3 h-3 mr-1" /> Generate Package
            </Button>
          )}
          {canApprove && (
            <Button size="sm" onClick={approvePackage} className="bg-green-700 hover:bg-green-800 text-white">
              <Zap className="w-3 h-3 mr-1" /> Approve All
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main sections */}
        <div className="lg:col-span-3 space-y-5">
          <ResumeSection pkg={pkg} candidate={candidate} />
          <SubmissionsSection
            pkg={pkg}
            candidate={candidate}
            submissions={submissions}
            excluded={excluded_firms.filter((e) =>
              submissions.every((s) => s.firm_name.toLowerCase() !== e.firm_name.toLowerCase())
            )}
          />
          <SkmSection
            pkg={pkg}
            candidate={candidate}
            skm_blast={skm_blast}
            excluded={excluded_firms}
          />
        </div>

        {/* Progress sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Review Checklist</h3>
            <ul className="space-y-2">
              {checklist.map((item) => (
                <li key={item.label} className="flex items-center gap-2 text-xs">
                  {item.done
                    ? <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                    : <div className="w-4 h-4 rounded-full border-2 border-gray-200 shrink-0" />}
                  <span className={item.done ? "text-gray-500 line-through" : "text-gray-700"}>{item.label}</span>
                </li>
              ))}
            </ul>

            {pkg.status === "completed" && pkg.approved_by && (
              <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400">
                Approved by {pkg.approved_by}
              </div>
            )}
          </div>

          {pkg.status === "generating" && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-xs text-blue-700">
              Package is being generated. Refresh in a moment.
            </div>
          )}

          {pkg.status === "failed" && (
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-xs text-red-600">
              Generation failed. Click "Generate Package" to retry.
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
